import apiService from './api.service';

export interface HeatmapData {
  clicks: Array<{
    x: number;
    y: number;
    pageX: number;
    pageY: number;
    element: string;
    pageUrl: string;
    timestamp: string;
  }>;
  scroll: Array<{
    depth: number;
    maxDepth: number;
    pageUrl: string;
    timestamp: string;
  }>;
  mouseMoves: Array<{
    x: number;
    y: number;
    pageUrl: string;
    timestamp: string;
  }>;
}

export interface PopularPage {
  url: string;
  views: number;
  uniqueVisitors: number;
  avgTime: number;
}

export interface ScrollDepthData {
  distribution: {
    '0-25': number;
    '25-50': number;
    '50-75': number;
    '75-100': number;
  };
  total: number;
  avgDepth: number;
}

export interface SessionData {
  id: string;
  sessionId: string;
  userId?: string;
  startedAt: string;
  endedAt?: string;
  duration?: number;
  device: {
    type?: string;
    os?: string;
    browser?: string;
  };
  location: {
    country?: string;
    city?: string;
  };
  pageViews: number;
  viewed?: boolean;
  pages: Array<{
    url: string;
    title?: string;
    timestamp: string;
  }>;
}

// Cache for in-flight screenshot requests (prevent duplicate calls)
const screenshotCache = new Map<string, Promise<{ screenshot: string; width: number; height: number } | null>>();

export const journeysService = {
  async getHeatmapData(
    applicationId: string,
    pageUrl?: string,
    startTime?: Date,
    endTime?: Date,
    deviceType?: string,
    platform?: string
  ): Promise<HeatmapData> {
    const params: any = {
      application_id: applicationId,
    };

    if (pageUrl) params.page_url = pageUrl;
    if (startTime) params.start_time = startTime.toISOString();
    if (endTime) params.end_time = endTime.toISOString();
    if (deviceType && deviceType !== 'all') params.device_type = deviceType;
    if (platform && platform !== 'all') params.platform = platform;

    const response = await apiService.get<any>('/v1/sdk/journeys/heatmap', {
      params,
    });

    return response.data;
  },

  async getPopularPages(
    applicationId: string,
    startTime?: Date,
    endTime?: Date,
    limit = 10,
    deviceType?: string,
    platform?: string
  ): Promise<PopularPage[]> {
    const params: any = {
      application_id: applicationId,
      limit,
    };

    if (startTime) params.start_time = startTime.toISOString();
    if (endTime) params.end_time = endTime.toISOString();
    if (deviceType && deviceType !== 'all') params.device_type = deviceType;
    if (platform && platform !== 'all') params.platform = platform;

    const response = await apiService.get<any>('/v1/sdk/journeys/popular-pages', {
      params,
    });

    return response.data;
  },

  async getScrollDepth(
    applicationId: string,
    pageUrl?: string,
    startTime?: Date,
    endTime?: Date,
    deviceType?: string,
    platform?: string
  ): Promise<ScrollDepthData> {
    const params: any = {
      application_id: applicationId,
    };

    if (pageUrl) params.page_url = pageUrl;
    if (startTime) params.start_time = startTime.toISOString();
    if (endTime) params.end_time = endTime.toISOString();
    if (deviceType && deviceType !== 'all') params.device_type = deviceType;
    if (platform && platform !== 'all') params.platform = platform;

    const response = await apiService.get<any>('/v1/sdk/journeys/scroll-depth', {
      params,
    });

    return response.data;
  },

  async getRecentSessions(
    applicationId: string,
    startTime?: Date,
    endTime?: Date,
    page = 1,
    limit = 10
  ): Promise<{ sessions: SessionData[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> {
    const params: any = {
      application_id: applicationId,
      page,
      limit,
    };

    if (startTime) params.start_time = startTime.toISOString();
    if (endTime) params.end_time = endTime.toISOString();

    const response = await apiService.get<any>('/v1/sdk/journeys/sessions', {
      params,
    });

    return {
      sessions: response.data,
      pagination: response.pagination,
    };
  },

  async getScreenshot(
    applicationId: string,
    pageUrl: string,
    deviceType?: string
  ): Promise<{ screenshot: string; width: number; height: number } | null> {
    // Create cache key
    const cacheKey = `${applicationId}:${pageUrl}:${deviceType || 'all'}`;

    // Check if there's already a request in-flight
    if (screenshotCache.has(cacheKey)) {
      console.log('[Journeys] Screenshot request already in-flight, reusing:', cacheKey);
      return screenshotCache.get(cacheKey)!;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        const params: any = {
          application_id: applicationId,
          page_url: pageUrl,
        };
        if (deviceType && deviceType !== 'all') params.device_type = deviceType;

        const response = await apiService.get<any>('/v1/sdk/journeys/screenshot', {
          params,
        });

        return response.data;
      } catch (error) {
        console.error('[Journeys] Failed to fetch screenshot:', error);
        return null;
      } finally {
        // Remove from cache after request completes (after 100ms to allow concurrent calls to reuse)
        setTimeout(() => {
          screenshotCache.delete(cacheKey);
        }, 100);
      }
    })();

    // Store in cache
    screenshotCache.set(cacheKey, requestPromise);

    return requestPromise;
  },
};
